/**
 * KayaSparsha — Unified App
 *
 * Routing:
 *   /            → Public marketing site (Home)
 *   /about       → About the clinic
 *   /services    → Services listing
 *   /treatments  → Treatment price menu
 *   /packages    → Wellness packages
 *   /doctor      → Meet the doctor
 *   /contact     → Location map + contact info
 *   /enquiry     → Place an enquiry (name, email, phone, message → email to support@)
 *   /admin/*     → Private clinic software (JWT-protected React app)
 */

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Public site
import PublicLayout from './public/PublicLayout';
import RouteMeta from './public/RouteMeta';
import Home from './public/pages/Home';
import About from './public/pages/About';
import Services from './public/pages/Services';
import Treatments from './public/pages/Treatments';
import Packages from './public/pages/Packages';
import Doctor from './public/pages/Doctor';
import Contact from './public/pages/Contact';
import Enquiry from './public/pages/Enquiry';
import NotFound from './public/pages/NotFound';

// Admin (clinic software)
import AdminApp from './admin/AdminApp';

/**
 * PageTracker — fires a GA4 page_view event on every public route change.
 * Must live inside <BrowserRouter> so useLocation() works.
 * Admin routes (/admin/*) are excluded to keep patient data off Google's servers.
 */
function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    // Skip if GA hasn't loaded (ad-blocker, or REACT_APP_GA_MEASUREMENT_ID not set)
    if (typeof window.gtag !== 'function') return;
    // Don't track private clinic software pages
    if (location.pathname.startsWith('/admin')) return;
    window.gtag('event', 'page_view', {
      page_path:     location.pathname + location.search,
      page_title:    document.title,
      page_location: window.location.href,
    });
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      {/* RouteMeta must come before PageTracker so GA4 sees the new title */}
      <RouteMeta />
      <PageTracker />
      <Routes>
        {/* Public marketing site */}
        <Route element={<PublicLayout />}>
          <Route index element={<Home />} />
          <Route path="about" element={<About />} />
          <Route path="services" element={<Services />} />
          <Route path="treatments" element={<Treatments />} />
          <Route path="packages" element={<Packages />} />
          <Route path="doctor" element={<Doctor />} />
          <Route path="contact" element={<Contact />} />
          <Route path="enquiry" element={<Enquiry />} />
          {/* Legacy redirect: old Book Now link used /contact#appointment */}
          <Route path="book" element={<Navigate to="/enquiry" replace />} />
        </Route>

        {/* Clinic software — private, JWT gated inside AdminApp */}
        <Route path="/admin/*" element={<AdminApp />} />

        {/* Legacy hash redirects */}
        <Route path="/index.html" element={<Navigate to="/" replace />} />

        {/* Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
